import { endOfDay, startOfDay } from 'date-fns';

import { ticketsList } from '~/data/generated/endpoints/tickets/tickets';
import { TicketsListEntryTypeItem, type TicketsListParams } from '~/data/generated/model';
import { toEnumArray } from '~/data/hooks/filterParams';
import { parseApiResponse } from '~/data/parseApiResponse';
import {
  type API_TicketFilterResponse,
  API_TicketFilterSearchParams,
  type TicketListMetadata,
  ticketSchema,
} from '~/data/schemas/ticket';

/**
 * Map the camelCase filter opts the table/hooks build onto the generated
 * snake_case query params. This is the camel→snake conversion (and the
 * start/end-of-day date coercion) the hand-written `LiftAPIClient.tickets.filter`
 * used to do; centralised here so both `useTickets` and `useTicketsForTable`
 * share it (AMP-502).
 */
export const toListParams = (opts: API_TicketFilterSearchParams): TicketsListParams => {
  const safe = API_TicketFilterSearchParams.parse(opts);

  return {
    ordering: safe.ordering,
    limit: safe.limit,
    offset: safe.offset,
    entry_type: toEnumArray(safe.entryType, TicketsListEntryTypeItem),
    search: safe.search,
    property: safe.property,
    car_id: safe.carId,
    cars: safe.cars,
    property_name: safe.propertyName,
    portfolio: safe.portfolios,
    service_provider: safe.serviceProviders,
    region: safe.regions,
    number: safe.number,
    started_time_after: safe.startedTimeAfter
      ? startOfDay(safe.startedTimeAfter).toJSON()
      : undefined,
    started_time_before: safe.startedTimeBefore
      ? endOfDay(safe.startedTimeBefore).toJSON()
      : undefined,
  };
};

/**
 * Fetch filtered tickets through the generated SDK. The list endpoint returns
 * the `{ count, metadata, results }` envelope when a `limit` is supplied, but a
 * bare `Ticket[]` when it isn't (e.g. the CSV export path). Normalise both into
 * `API_TicketFilterResponse`, mirroring the old client. Both paths parse their
 * ticket results through `ticketSchema` so coercion, key-stripping, and drift
 * diagnostics apply consistently. Only the metadata envelope is tightened
 * relative to the hand-written `API_TicketFilterResponse` still consumed by the
 * grid, so contain that ripple with a documented boundary cast rather than
 * retyping the table.
 */
export const fetchTicketsFilter = async (
  opts: API_TicketFilterSearchParams,
): Promise<API_TicketFilterResponse> => {
  const { data } = await ticketsList(toListParams(opts));

  if (Array.isArray(data)) {
    return {
      results: parseApiResponse(ticketSchema.array(), data, 'Ticket'),
      count: data.length,
      metadata: {} as TicketListMetadata,
    };
  }

  // `data?.` throughout so a nullish/malformed envelope surfaces as a schema
  // error from `parseApiResponse` rather than throwing on property access.
  return {
    results: parseApiResponse(ticketSchema.array(), data?.results, 'Ticket'),
    // The `results` parse above throws first on a nullish envelope, so this
    // `?? 0` is unreachable at runtime — it only satisfies the non-null `count`.
    count: data?.count ?? 0,
    metadata: data?.metadata as unknown as TicketListMetadata,
  };
};
